/**
 * Sprint Funnel · #1 — Inbox de Leads del negocio.
 *
 * /admin/leads
 *   · Lista paginada + filtros (estado, búsqueda)
 *   · Detalle en panel lateral con quick-actions (tel:, mailto:, wa.me)
 *   · Cambio de estado con audit history
 *   · Notas internas (acumulativas)
 *   · "Marcar todos como leídos"
 */
import { useEffect, useState } from "react";
import { api, fmtDateTime } from "../lib/api";
import {
  EnvelopeSimple, Phone, WhatsappLogo, CheckCircle, Warning,
  TrashSimple, Note, ClockClockwise, MagnifyingGlass, X, ArrowsClockwise,
  Tray, ChatCircleText, Plus,
} from "@phosphor-icons/react";

const STATUS_META = {
  new:       { label: "Nuevo",      color: "text-amber-300",  bg: "bg-amber-500/10",  border: "border-amber-500/30",  emoji: "•" },
  contacted: { label: "Contactado", color: "text-sky-300",    bg: "bg-sky-500/10",    border: "border-sky-500/30",    emoji: "✓" },
  converted: { label: "Convertido", color: "text-emerald-300",bg: "bg-emerald-500/10",border: "border-emerald-500/30",emoji: "★" },
  spam:      { label: "Spam",       color: "text-red-300",    bg: "bg-red-500/10",    border: "border-red-500/30",    emoji: "✗" },
};

export default function LeadsInbox() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ unread: 0, by_status: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [selected, setSelected] = useState(null);  // lead detail in drawer

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const _status = opts.status !== undefined ? opts.status : statusFilter;
      const _q = opts.q !== undefined ? opts.q : q;
      const _page = opts.page !== undefined ? opts.page : page;
      if (_status) params.set("status", _status);
      if (_q) params.set("q", _q);
      params.set("page", String(_page));
      params.set("page_size", "25");
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get(`/admin/leads?${params.toString()}`),
        api.get("/admin/leads/summary"),
      ]);
      setItems(list.items || []);
      setPages(list.pages || 1);
      setSummary(sum);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Cargar al montar y cada vez que cambian filtros / página
    load(); /* eslint-disable-line react-hooks/exhaustive-deps */
  }, [statusFilter, q, page]);

  const openDetail = async (lead) => {
    try {
      const { data } = await api.get(`/admin/leads/${lead.id}`);
      setSelected(data);
      // refrescar lista (porque marcado leído modifica is_unread)
      load();
    } catch (e) { console.error(e); }
  };

  const onStatusChange = async (newStatus, note = "") => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/admin/leads/${selected.id}`, { status: newStatus, note: note || undefined });
      setSelected(data);
      load();
    } catch (e) { console.error(e); }
  };

  const onAddNote = async (text) => {
    if (!selected || !text.trim()) return;
    try {
      await api.post(`/admin/leads/${selected.id}/notes`, { text });
      const { data } = await api.get(`/admin/leads/${selected.id}`);
      setSelected(data);
    } catch (e) { console.error(e); }
  };

  const markAllRead = async () => {
    try {
      await api.post("/admin/leads/mark-all-read", {});
      // Optimistic: cero unread + actualizar lista para reflejar read_at
      setSummary((s) => ({ ...s, unread: 0 }));
      setItems((items) => items.map((l) => ({ ...l, is_unread: false, read_at: l.read_at || new Date().toISOString() })));
      // Re-fetch para sincronizar con la verdad del servidor
      load();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6" data-testid="leads-inbox">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Bandeja de Leads</h1>
          <p className="text-sm text-[var(--ag-text-dim)] mt-1">
            Solicitudes de cita y consultas que llegan desde tu perfil público.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[var(--ag-text-dim)] hover:text-white border border-[var(--ag-border)] rounded transition-colors"
            data-testid="leads-refresh-btn"
          >
            <ArrowsClockwise size={14} /> Refrescar
          </button>
          {summary.unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-[var(--ag-gold)] text-[var(--ag-midnight)] rounded hover:bg-[var(--ag-gold-strong)] transition-colors"
              data-testid="leads-mark-all-read-btn"
            >
              <CheckCircle size={14} weight="fill" /> Marcar todos leídos
            </button>
          )}
        </div>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="leads-kpi-row">
        <KpiCard label="Total" value={summary.total} testId="kpi-total" />
        <KpiCard label="No leídos" value={summary.unread} highlight testId="kpi-unread" />
        <KpiCard label="Nuevos" value={summary.by_status.new || 0} testId="kpi-new" />
        <KpiCard label="Contactados" value={summary.by_status.contacted || 0} testId="kpi-contacted" />
        <KpiCard label="Convertidos" value={summary.by_status.converted || 0} testId="kpi-converted" />
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex items-center gap-1 flex-wrap" data-testid="leads-status-tabs">
          <FilterChip label="Todos" active={!statusFilter} onClick={() => { setStatusFilter(""); setPage(1); }} testId="filter-all" />
          <FilterChip label="Nuevos" active={statusFilter === "new"} onClick={() => { setStatusFilter("new"); setPage(1); }} testId="filter-new" />
          <FilterChip label="Contactados" active={statusFilter === "contacted"} onClick={() => { setStatusFilter("contacted"); setPage(1); }} testId="filter-contacted" />
          <FilterChip label="Convertidos" active={statusFilter === "converted"} onClick={() => { setStatusFilter("converted"); setPage(1); }} testId="filter-converted" />
          <FilterChip label="Spam" active={statusFilter === "spam"} onClick={() => { setStatusFilter("spam"); setPage(1); }} testId="filter-spam" />
        </div>
        <div className="relative flex-1 max-w-md sm:ml-auto">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Buscar por nombre, email, teléfono…"
            className="w-full pl-9 pr-3 py-2 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded text-sm outline-none focus:border-[var(--ag-gold)]/60"
            data-testid="leads-search-input"
          />
        </div>
      </div>

      {/* Tabla / lista */}
      <div className="border border-[var(--ag-border)] rounded-lg overflow-hidden bg-[var(--ag-midnight-soft)]/40">
        {loading ? (
          <div className="p-10 text-center text-[var(--ag-text-dim)] text-sm" data-testid="leads-loading">Cargando…</div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="divide-y divide-[var(--ag-border)]">
            {items.map((lead) => (
              <LeadRow key={lead.id} lead={lead} onClick={() => openDetail(lead)} />
            ))}
          </div>
        )}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm" data-testid="leads-pagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 border border-[var(--ag-border)] rounded disabled:opacity-40 hover:text-white">‹</button>
          <span className="text-[var(--ag-text-dim)]">{page} / {pages}</span>
          <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 border border-[var(--ag-border)] rounded disabled:opacity-40 hover:text-white">›</button>
        </div>
      )}

      {selected && <LeadDrawer lead={selected} onClose={() => setSelected(null)} onStatusChange={onStatusChange} onAddNote={onAddNote} />}
    </div>
  );
}

/* ---------------- Subcomponents ---------------- */

function KpiCard({ label, value, highlight, testId }) {
  return (
    <div
      className={`p-3 rounded-lg border ${
        highlight && value > 0
          ? "bg-[var(--ag-gold)]/10 border-[var(--ag-gold)]/40 text-[var(--ag-gold)]"
          : "bg-[var(--ag-midnight-soft)]/40 border-[var(--ag-border)]"
      }`}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function FilterChip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
        active
          ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] border-[var(--ag-gold)]"
          : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"
      }`}
    >
      {label}
    </button>
  );
}

function LeadRow({ lead, onClick }) {
  const meta = STATUS_META[lead.status] || STATUS_META.new;
  const unread = lead.is_unread;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 hover:bg-[var(--ag-midnight)]/40 transition-colors flex items-start gap-3 ${unread ? "" : "opacity-80"}`}
      data-testid={`lead-row-${lead.id}`}
    >
      <div className={`mt-1 w-2 h-2 rounded-full ${unread ? "bg-[var(--ag-gold)]" : "bg-transparent"}`} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-semibold text-sm ${unread ? "text-white" : "text-[var(--ag-text)]"}`}>{lead.name}</span>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded ${meta.bg} ${meta.color} border ${meta.border}`}>
            {meta.label}
          </span>
          {lead.service && (
            <span className="text-[11px] text-[var(--ag-text-dim)] truncate">· {lead.service}</span>
          )}
        </div>
        {lead.message && (
          <p className="text-xs text-[var(--ag-text-dim)] mt-1 line-clamp-2">{lead.message}</p>
        )}
        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[var(--ag-text-dim)]">
          {lead.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {lead.phone}</span>}
          {lead.email && <span className="inline-flex items-center gap-1"><EnvelopeSimple size={11} /> {lead.email}</span>}
        </div>
      </div>
      <div className="text-[11px] text-[var(--ag-text-dim)] whitespace-nowrap tabular-nums">{fmtDateTime(lead.created_at)}</div>
    </button>
  );
}

function EmptyState() {
  return (
    <div className="p-12 text-center" data-testid="leads-empty">
      <Tray size={40} className="mx-auto text-[var(--ag-text-dim)]" />
      <h3 className="mt-3 text-base font-semibold">Aún no tienes leads</h3>
      <p className="mt-1 text-sm text-[var(--ag-text-dim)] max-w-md mx-auto">
        Cuando un cliente envíe el formulario &quot;Solicitar cita&quot; desde tu perfil público,
        aparecerá aquí. Comparte tu perfil para empezar a recibir consultas.
      </p>
    </div>
  );
}

function LeadDrawer({ lead, onClose, onStatusChange, onAddNote }) {
  const [noteText, setNoteText] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const meta = STATUS_META[lead.status] || STATUS_META.new;
  const waNumber = (lead.phone || "").replace(/[^0-9]/g, "");

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="lead-drawer-overlay" aria-hidden />
      <aside className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[480px] bg-[var(--ag-midnight)] border-l border-[var(--ag-border)] overflow-y-auto" data-testid="lead-drawer">
        <header className="sticky top-0 bg-[var(--ag-midnight)] border-b border-[var(--ag-border)] p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold" data-testid="drawer-lead-name">{lead.name}</h2>
            <p className="text-[11px] text-[var(--ag-text-dim)] tabular-nums">{fmtDateTime(lead.created_at)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="p-2 hover:bg-[var(--ag-midnight-soft)] rounded" data-testid="drawer-close-btn">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-5">
          {/* Estado actual */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Estado</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs uppercase tracking-wider rounded ${meta.bg} ${meta.color} border ${meta.border}`} data-testid="drawer-current-status">
              {meta.label}
            </span>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-3 gap-2">
            <ActionBtn
              href={lead.phone ? `tel:${lead.phone}` : undefined}
              icon={Phone}
              label="Llamar"
              disabled={!lead.phone}
              testId="drawer-action-call"
            />
            <ActionBtn
              href={lead.email ? `mailto:${lead.email}` : undefined}
              icon={EnvelopeSimple}
              label="Email"
              disabled={!lead.email}
              testId="drawer-action-email"
            />
            <ActionBtn
              href={waNumber ? `https://wa.me/${waNumber}` : undefined}
              icon={WhatsappLogo}
              label="WhatsApp"
              disabled={!waNumber}
              testId="drawer-action-whatsapp"
              external
            />
          </div>

          {/* Datos */}
          <section className="space-y-2 border border-[var(--ag-border)] rounded-lg p-3 bg-[var(--ag-midnight-soft)]/30">
            <Row label="Servicio" value={lead.service || "—"} testId="drawer-service" />
            <Row label="Teléfono" value={lead.phone || "—"} testId="drawer-phone" />
            <Row label="Email" value={lead.email || "—"} testId="drawer-email" />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[var(--ag-text-dim)]">Mensaje</div>
              <p className="text-sm mt-1 whitespace-pre-line" data-testid="drawer-message">{lead.message || <em className="text-[var(--ag-text-dim)]">(sin mensaje)</em>}</p>
            </div>
          </section>

          {/* Cambio de estado */}
          <section>
            <div className="text-[11px] uppercase tracking-wider text-[var(--ag-text-dim)] mb-2">Cambiar estado</div>
            <div className="grid grid-cols-2 gap-2" data-testid="drawer-status-actions">
              <StatusBtn label="Contactado" testId="status-contacted" active={lead.status === "contacted"} onClick={() => onStatusChange("contacted", statusNote)} />
              <StatusBtn label="Convertido" testId="status-converted" active={lead.status === "converted"} onClick={() => onStatusChange("converted", statusNote)} icon={CheckCircle} variant="success" />
              <StatusBtn label="Spam" testId="status-spam" active={lead.status === "spam"} onClick={() => onStatusChange("spam", statusNote)} icon={TrashSimple} variant="danger" />
              <StatusBtn label="Volver a Nuevo" testId="status-new" active={lead.status === "new"} onClick={() => onStatusChange("new", statusNote)} variant="ghost" />
            </div>
            <input
              type="text"
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              placeholder="Nota opcional al cambiar de estado…"
              className="mt-2 w-full px-3 py-2 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded text-sm outline-none focus:border-[var(--ag-gold)]/60"
              data-testid="drawer-status-note-input"
            />
          </section>

          {/* Notas internas */}
          <section>
            <div className="text-[11px] uppercase tracking-wider text-[var(--ag-text-dim)] mb-2 flex items-center gap-1">
              <Note size={12} /> Notas internas ({(lead.notes || []).length})
            </div>
            <div className="space-y-2" data-testid="drawer-notes-list">
              {(lead.notes || []).length === 0 && (
                <p className="text-xs text-[var(--ag-text-dim)] italic">Sin notas aún.</p>
              )}
              {(lead.notes || []).map((n) => (
                <div key={n.id} className="p-2 bg-[var(--ag-midnight-soft)]/40 border border-[var(--ag-border)] rounded text-xs" data-testid={`drawer-note-${n.id}`}>
                  <div className="flex items-center justify-between gap-2 text-[10px] text-[var(--ag-text-dim)] mb-1">
                    <span>{n.by_user_name}</span>
                    <span className="tabular-nums">{fmtDateTime(n.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-line">{n.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Añadir nota…"
                className="flex-1 px-3 py-2 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded text-sm outline-none focus:border-[var(--ag-gold)]/60"
                data-testid="drawer-add-note-input"
              />
              <button
                type="button"
                onClick={() => { onAddNote(noteText); setNoteText(""); }}
                disabled={!noteText.trim()}
                className="px-3 py-2 bg-[var(--ag-gold)] text-[var(--ag-midnight)] rounded text-sm font-semibold hover:bg-[var(--ag-gold-strong)] disabled:opacity-40"
                data-testid="drawer-add-note-btn"
              >
                <Plus size={14} />
              </button>
            </div>
          </section>

          {/* Historial */}
          <section>
            <div className="text-[11px] uppercase tracking-wider text-[var(--ag-text-dim)] mb-2 flex items-center gap-1">
              <ClockClockwise size={12} /> Historial
            </div>
            {(lead.history || []).length === 0 ? (
              <p className="text-xs text-[var(--ag-text-dim)] italic">Sin cambios registrados.</p>
            ) : (
              <ul className="space-y-1.5 text-xs" data-testid="drawer-history-list">
                {(lead.history || []).slice().reverse().map((h) => (
                  <li key={h.id} className="flex items-start gap-2 text-[var(--ag-text-dim)]">
                    <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-[var(--ag-text-dim)]/40 flex-shrink-0" />
                    <span className="flex-1">
                      <strong className="text-[var(--ag-text)]">{h.action === "status_change" ? `${h.from} → ${h.to}` : "Nota añadida"}</strong>
                      <span className="text-[10px]"> · {h.by_user_name} · {fmtDateTime(h.at)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </aside>
    </>
  );
}

function ActionBtn({ href, icon: Icon, label, disabled, testId, external }) {
  if (disabled) {
    return (
      <button type="button" disabled className="flex flex-col items-center justify-center gap-1 p-3 border border-[var(--ag-border)] rounded text-xs text-[var(--ag-text-dim)] opacity-40 cursor-not-allowed" data-testid={testId}>
        <Icon size={18} /> {label}
      </button>
    );
  }
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="flex flex-col items-center justify-center gap-1 p-3 border border-[var(--ag-gold)]/30 hover:border-[var(--ag-gold)] bg-[var(--ag-gold)]/5 hover:bg-[var(--ag-gold)]/10 rounded text-xs text-[var(--ag-gold)] transition-colors"
      data-testid={testId}
    >
      <Icon size={18} weight="duotone" /> {label}
    </a>
  );
}

function StatusBtn({ label, active, onClick, icon: Icon, variant, testId }) {
  let cls = "border border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white";
  if (active) cls = "bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-bold border-[var(--ag-gold)]";
  else if (variant === "success") cls = "border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10";
  else if (variant === "danger")  cls = "border border-red-500/40 text-red-300 hover:bg-red-500/10";
  else if (variant === "ghost")   cls = "border border-dashed border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white";
  return (
    <button type="button" onClick={onClick} className={`px-3 py-2 text-xs font-medium rounded inline-flex items-center justify-center gap-1.5 transition-colors ${cls}`} data-testid={testId}>
      {Icon && <Icon size={14} />} {label}
    </button>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-[11px] uppercase tracking-wider text-[var(--ag-text-dim)]">{label}</span>
      <span className="text-right truncate" data-testid={testId}>{value}</span>
    </div>
  );
}
